import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
} from '@mui/material';
import COLORS from '../../../../resources/style/Themes';

export default function EditSupportLinkModal({
  show,
  onHide,
  supportLink,
  onSave,
}) {
  const [header, setHeader] = useState('');
  const [linkType, setLinkType] = useState('');
  const [name, setName] = useState('');
  const [isActive, setIsActive] = useState('');
  const [sectionType, setSectionType] = useState('');
  const [url, setUrl] = useState('');
  const [languageCode, setLanguageCode] = useState('');
  const [validation, setValidation] = useState({});

  useEffect(() => {
    if (supportLink && show) {
      setHeader(supportLink.header);
      setIsActive(supportLink.is_active);
      setLinkType(supportLink.link_type);
      setName(supportLink.name);
      setSectionType(supportLink.section_type);
      setUrl(supportLink.url);
      setLanguageCode(supportLink.language_code);
    }
  }, [supportLink, show]);

  useEffect(() => {
    if (!show) {
      setHeader('');
      setIsActive('');
      setLinkType('');
      setName('');
      setSectionType('');
      setUrl('');
      setLanguageCode('');
      setValidation({});
    }
  }, []);

  const handleSave = () => {
    if (!validateFields()) {
      return;
    }
    const updatedSupportLink = {
      header: header,
      is_active: isActive,
      link_type: linkType,
      name: name,
      section_type: sectionType,
      url: url,
      language_code: languageCode,
      id: supportLink.id,
    };
    onSave(updatedSupportLink);
    resetValues();
    onHide();
  };

  const resetValues = () => {
    setHeader('');
    setLinkType('');
    setName('');
    setSectionType('');
    setUrl('');
    setIsActive('');
    setLanguageCode('');
    setValidation({});
  };

  const validateFields = () => {
    let isValid = true;
    const newValidation = {};

    if (!header || header.trim().length === 0) {
      newValidation.header = 'error';
      isValid = false;
    }
    if (!name || name.trim().length === 0) {
      newValidation.name = 'error';
      isValid = false;
    }
    if (!url || url.trim().length === 0) {
      newValidation.url = 'error';
      isValid = false;
    }
    if (!linkType) {
      newValidation.linkType = 'error';
      isValid = false;
    }
    if (!sectionType) {
      newValidation.sectionType = 'error';
      isValid = false;
    }
    if (!languageCode) {
      newValidation.languageCode = 'error';
      isValid = false;
    }
    setValidation(newValidation);
    return isValid;
  };

  return (
    <Modal
      open={show}
      onClose={() => {
        onHide();
        resetValues();
      }}
      aria-labelledby="edit-support-link-modal-title"
      aria-describedby="edit-support-link-modal-description"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          width: { xs: '90%', sm: '80%', md: '60%', lg: '50%' },
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            backgroundColor: COLORS.SAFEGUARD_BLUE,
            color: COLORS.WHITE,
            p: 2,
          }}
        >
          <Typography
            id="edit-support-link-modal-title"
            variant="h6"
            component="div"
          >
            Edit {header}
          </Typography>
        </Box>
        <Box component="form" sx={{ p: 4 }}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <TextField
              label="Header"
              variant="outlined"
              value={header}
              onChange={(e) => setHeader(e.target.value)}
              error={validation.header === 'error'}
              helperText={
                validation.header === 'error' ? 'Header cannot be empty.' : ''
              }
            />
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <TextField
              label="Name"
              variant="outlined"
              value={name}
              onChange={(e) => setName(e.target.value)}
              error={validation.name === 'error'}
              helperText={
                validation.name === 'error' ? 'Name cannot be empty.' : ''
              }
            />
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <TextField
              label="URL"
              variant="outlined"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              error={validation.url === 'error'}
              helperText={
                validation.url === 'error' ? 'URL cannot be empty.' : ''
              }
            />
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Active</InputLabel>
            <Select
              value={isActive}
              onChange={(e) => setIsActive(e.target.value)}
              label="Active"
              error={validation.isActive === 'error'}
            >
              <MenuItem value={true}>True</MenuItem>
              <MenuItem value={false}>False</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Link Type</InputLabel>
            <Select
              value={linkType}
              onChange={(e) => setLinkType(e.target.value)}
              label="Link Type"
              error={validation.linkType === 'error'}
            >
              <MenuItem value="PDF">PDF</MenuItem>
              <MenuItem value="video">Video</MenuItem>
              <MenuItem value="web_page">Web Page</MenuItem>
            </Select>
            {validation.linkType === 'error' && (
              <Typography variant="body2" color="error">
                Select a link type.
              </Typography>
            )}
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Section Type</InputLabel>
            <Select
              value={sectionType}
              onChange={(e) => setSectionType(e.target.value)}
              label="Section Type"
              error={validation.sectionType === 'error'}
            >
              <MenuItem value="" disabled>
                Select Section Type
              </MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="legal">Legal</MenuItem>
              <MenuItem value="support">Support</MenuItem>
            </Select>
            {validation.sectionType === 'error' && (
              <Typography variant="body2" color="error">
                Select a section type.
              </Typography>
            )}
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Language Code</InputLabel>
            <Select
              value={languageCode}
              onChange={(e) => setLanguageCode(e.target.value)}
              label="Language Code"
              error={validation.languageCode === 'error'}
            >
              <MenuItem value="" disabled>
                Select Language Code
              </MenuItem>
              <MenuItem value="dan">Danish</MenuItem>
              <MenuItem value="eng">English</MenuItem>
              <MenuItem value="fin">Finnish</MenuItem>
              <MenuItem value="fra">French</MenuItem>
              <MenuItem value="deu">German</MenuItem>
              <MenuItem value="ell">Greek</MenuItem>
              <MenuItem value="nob">Norwegian</MenuItem>
              <MenuItem value="pol">Polish</MenuItem>
              <MenuItem value="por">Portuguese</MenuItem>
              <MenuItem value="spa">Spanish</MenuItem>
              <MenuItem value="swe">Swedish</MenuItem>
            </Select>
            {validation.languageCode === 'error' && (
              <Typography variant="body2" color="error">
                Select a language code.
              </Typography>
            )}
          </FormControl>
        </Box>
        <Box sx={{ textAlign: 'right', p: 2 }}>
          <Button
            variant="contained"
            onClick={handleSave}
          >
            Save
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
